// src/hooks/useAffiliate.js
// Custom hook for affiliate functionality

import { useState, useEffect } from 'react';
import { 
  getAffiliateProfile, 
  createAffiliateProfile, 
  updateAffiliateStats,
  validateAffiliateCode,
  createAffiliateLink
} from '../lib/affiliateSchema';

export default function useAffiliate(userId) {
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      loadAffiliateProfile();
    }
  }, [userId]);

  const loadAffiliateProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const affiliateData = await getAffiliateProfile(userId);
      setAffiliate(affiliateData);
    } catch (err) {
      console.error('Error loading affiliate profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const newAffiliate = await createAffiliateProfile(userId, userData);
      setAffiliate(newAffiliate);
      return newAffiliate;
    } catch (err) {
      console.error('Error creating affiliate profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!affiliate) throw new Error('No affiliate profile found');
      
      await updateAffiliateStats(affiliate.affiliateId, updates);
      setAffiliate(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error('Error updating affiliate profile:', err);
      setError(err.message);
      throw err;
    }
  };

  const validateCode = async (referralCode) => {
    try {
      return await validateAffiliateCode(referralCode);
    } catch (err) {
      console.error('Error validating affiliate code:', err);
      setError(err.message);
      throw err;
    }
  };

  const generateLink = (productId = null) => {
    if (!affiliate) return null;
    
    const baseUrl = window.location.origin;
    return createAffiliateLink(baseUrl, affiliate.referralCode, productId);
  };

  return {
    affiliate,
    loading,
    error,
    createProfile,
    updateProfile,
    validateCode,
    generateLink,
    refresh: loadAffiliateProfile
  };
}
